import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { FileText, Pencil, Upload, Eye, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Report } from "../types";
import { useUserRoles } from "@/hooks/useUserRoles";

interface ReportListProps {
  reports: Report[];
  isLoading: boolean;
  isSubmissions?: boolean;
  teamId?: string;
  onReportUpdated?: () => void;
}

export const ReportList = ({ 
  reports, 
  isLoading, 
  isSubmissions = false, 
  teamId,
  onReportUpdated 
}: ReportListProps) => {
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEditClick = (report: Report) => {
    setEditingReport(report);
    setTitle(report.title);
    setContent(report.content || "");
    setImageFile(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleDeleteReport = async (reportId: string, isSubmission = false) => {
    try {
      const tableName = isSubmission ? 'report_submissions' : 'reports';
      
      const { error } = await supabase
        .from(tableName as any)
        .delete()
        .eq('id', reportId);
        
      if (error) throw error;
      
      toast({
        title: `${isSubmission ? 'Submission' : 'Report'} deleted`,
        description: `The ${isSubmission ? 'submission' : 'report'} has been deleted successfully.`
      });
      
      if (onReportUpdated) {
        onReportUpdated();
      }
    } catch (error: any) {
      toast({
        title: "Failed to delete",
        description: error.message || "An error occurred while deleting.",
        variant: "destructive"
      });
    }
  };

  const handleSaveChanges = async () => {
    if (!editingReport) return;
    
    try {
      setIsSubmitting(true);
      
      const updateData: Partial<Report> = {
        title,
        content
      };
      
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `report_images/${fileName}`;
        
        console.log('Uploading image:', filePath);
        
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('reports')
          .upload(filePath, imageFile);
          
        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          throw uploadError;
        }
        
        console.log('Upload successful:', uploadData);
        
        const { data: urlData } = supabase
          .storage
          .from('reports')
          .getPublicUrl(filePath);
          
        updateData.image_url = urlData.publicUrl;
      }
      
      console.log('Updating report with data:', updateData);
      
      const isReportSubmission = isSubmissions || 'form_id' in editingReport;
      const tableName = isReportSubmission ? 'report_submissions' : 'reports';
      
      const { error, data } = await supabase
        .from(tableName as any)
        .update(updateData)
        .eq('id', editingReport.id)
        .select();
        
      if (error) {
        console.error(`Error updating ${tableName}:`, error);
        throw error;
      }
      
      console.log('Update successful:', data);
      
      toast({
        title: `${isReportSubmission ? 'Submission' : 'Report'} updated`,
        description: `The ${isReportSubmission ? 'submission' : 'report'} has been updated successfully.`
      });
      
      if (onReportUpdated) {
        onReportUpdated();
      }
    } catch (error: any) {
      console.error('Error during update:', error);
      toast({
        title: "Failed to update",
        description: error.message || "An error occurred while updating.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      setEditingReport(null);
    }
  };

  const handleViewReport = (report: Report) => {
    if (report.content) {
      try {
        const url = new URL(report.content);
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
      } catch (error) {
        toast({
          title: "Invalid URL",
          description: "The report link is not a valid URL.",
          variant: "destructive"
        });
      }
    } else {
      toast({
        title: "No link available",
        description: "This report does not have an associated link.",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-10">Loading reports...</div>;
  }

  if (reports.length === 0) {
    return <div className="text-center py-10 text-muted-foreground">No reports found.</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {reports.map((report) => {
        const isReportSubmission = isSubmissions || 'form_id' in report;
        
        return (
          <Card key={report.id} className="overflow-hidden border-2 hover:shadow-lg transition-shadow h-full flex flex-col bg-background/50">
            <div className="p-6 flex flex-col h-full">
              <div className="flex flex-col items-center justify-center mb-4">
                <div className="p-2 bg-primary/10 text-primary rounded-full mb-2">
                  <img 
                    src="/lovable-uploads/b476de88-d1fb-48c9-8796-68a63cc74f68.png" 
                    alt="Shield icon" 
                    className="w-5 h-5"
                  />
                </div>
                
                <h3 className="font-medium text-lg text-center">{report.title}</h3>
              </div>
              
              {isAdmin && (
                <div className="flex justify-center mt-2 mb-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex items-center gap-1" 
                          onClick={() => handleEditClick(report)}
                        >
                          <Pencil className="w-4 h-4" />
                          <span>Edit</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit {isReportSubmission ? 'submission' : 'report'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              
              <div className="flex-1 mb-5 flex flex-col items-center">
                <img 
                  src={report.image_url || "/lovable-uploads/251380b1-4d53-4124-bf15-25f08257a46b.png"} 
                  alt="Report illustration" 
                  className="w-32 h-32 object-contain mb-3"
                />
                <p className="text-sm text-muted-foreground line-clamp-3 mt-2 text-center">
                  Click view to open the report
                </p>
              </div>
            </div>
            
            <div className="flex flex-col w-full">
              <Button 
                className="w-full rounded-none h-12 text-base bg-primary text-white hover:bg-primary-600"
                onClick={() => handleViewReport(report)}
              >
                <Eye className="w-5 h-5 mr-2" />
                View
              </Button>
              
              {isAdmin && (
                <Button 
                  className="w-full rounded-none h-12 text-base bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => handleDeleteReport(report.id, isReportSubmission)}
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </Card>
        );
      })}

      <Dialog open={!!editingReport} onOpenChange={(open) => !open && setEditingReport(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {editingReport && 'form_id' in editingReport ? 'Submission' : 'Report'}</DialogTitle>
            <DialogDescription>
              Make changes to your {editingReport && 'form_id' in editingReport ? 'submission' : 'report'} here. Click save when you're done.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="content">Link URL</Label>
              <Input
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="image">Report Image</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={() => document.getElementById('image')?.click()}
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
              {imageFile && (
                <p className="text-sm text-muted-foreground">
                  New image selected: {imageFile.name}
                </p>
              )}
              {editingReport?.image_url && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground mb-2">Current image:</p>
                  <img 
                    src={editingReport.image_url} 
                    alt="Current report image" 
                    className="w-full max-h-32 object-contain border rounded-md"
                  />
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditingReport(null)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveChanges}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
